import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Trash2, Calendar } from 'lucide-react';

const PaymentHistoryTable = ({ transactions, onDelete, userRole = 'admin' }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMethod, setFilterMethod] = useState('');
    const [filteredTransactions, setFilteredTransactions] = useState([]);

    useEffect(() => {
        if (!transactions) {
            setFilteredTransactions([]);
            return;
        }

        let result = [...transactions];

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(t =>
                (t.customer_name && t.customer_name.toLowerCase().includes(term)) ||
                (t.plan_key && t.plan_key.toLowerCase().includes(term)) ||
                (t.description && t.description.toLowerCase().includes(term)) ||
                (t.payment_method && t.payment_method.toLowerCase().includes(term))
            );
        }

        // Filter by payment method
        if (filterMethod) {
            const fm = filterMethod.toLowerCase();
            result = result.filter(t => {
                if (!t.payment_method) return false;
                const pm = t.payment_method.toLowerCase();

                if (fm === 'cash' && pm === 'cash') return true;
                if (fm === 'e-transfer' && (pm === 'e-transfer' || pm === 'etransfer')) return true;
                if (fm === 'credit card' && (pm === 'credit card' || pm === 'card' || pm === 'stripe')) return true;
                if (fm === 'cheque' && pm === 'cheque') return true;
                if (fm === 'manual' && (pm === 'manual' || pm === 'other')) return true;

                return pm === fm;
            });
        }

        setFilteredTransactions(result);
    }, [transactions, searchTerm, filterMethod]);

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toFixed(2)}`;
    };

    const getMethodBadgeColor = (method) => {
        const lowerMethod = (method || '').toLowerCase();
        if (lowerMethod === 'cash') return 'bg-green-100 text-green-800 border-green-200';
        if (lowerMethod === 'e-transfer' || lowerMethod === 'etransfer') return 'bg-blue-100 text-blue-800 border-blue-200';
        if (lowerMethod === 'credit card' || lowerMethod === 'card' || lowerMethod === 'stripe') return 'bg-purple-100 text-purple-800 border-purple-200';
        if (lowerMethod === 'cheque') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        return 'bg-gray-100 text-gray-800 border-gray-200';
    };

    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            {/* Header / Toolbar */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="relative w-full sm:w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={18} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search transactions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                    />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative">
                        <select
                            value={filterMethod}
                            onChange={(e) => setFilterMethod(e.target.value)}
                            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm appearance-none bg-white"
                        >
                            <option value="">All Methods</option>
                            <option value="Cash">Cash</option>
                            <option value="E-Transfer">E-Transfer</option>
                            <option value="Credit Card">Credit Card</option>
                            <option value="Cheque">Cheque</option>
                            <option value="Manual">Manual</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                            <Filter size={16} className="text-gray-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Details
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Method
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                            </th>
                            {userRole === 'admin' && (
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTransactions.length === 0 ? (
                            <tr>
                                <td colSpan={userRole === 'admin' ? 6 : 5} className="px-6 py-8 text-center text-gray-500 text-sm">
                                    No transactions found matching your criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredTransactions.map((transaction) => (
                                <tr key={transaction._id || transaction.transaction_id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {transaction.payment_date ? new Date(transaction.payment_date).toLocaleString('en-CA', {
                                            timeZone: 'America/Toronto',
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        }) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{transaction.customer_name}</div>
                                        {transaction.customer_email && (
                                            <div className="text-xs text-gray-500">{transaction.customer_email}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <div className="max-w-xs truncate" title={transaction.plan_key || transaction.description}>
                                            {transaction.plan_key || transaction.description || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getMethodBadgeColor(transaction.payment_method)}`}>
                                            {transaction.payment_method || 'Manual'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        {formatCurrency(transaction.total_amount)}
                                    </td>
                                    {userRole === 'admin' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => onDelete(transaction._id || transaction.transaction_id)}
                                                className="text-red-400 hover:text-red-900 transition-colors tooltip"
                                                title="Delete Transaction"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between">
                <span>Total Transactions: {filteredTransactions.length}</span>
                <span className="font-semibold text-gray-900">
                    Total Value: {formatCurrency(filteredTransactions.reduce((sum, t) => sum + parseFloat(t.total_amount || 0), 0))}
                </span>
            </div>
        </div>
    );
};

export default PaymentHistoryTable;
